import {
  PLOT_MATERIALS,
  STICKER_PRESETS,
  TUBE_PRESETS,
  calculateEffectiveDevelopmentMm,
  createCustomStickerProductV1,
  createPresetStickerProductV1,
  getPlotMaterial,
  getStickerPreset,
  getTubePreset,
  resolveTubeProduct,
} from '@dg-embalajes/product-domain';
import { useState } from 'react';
import type { EditorPort, EditorSnapshot } from '@/ports/editor';
import { Modal } from '../Modal';
import { SegmentedControl } from '../SegmentedControl';

export function ProductSettingsDialog({ editor, snapshot, onClose }: { readonly editor: EditorPort; readonly snapshot: EditorSnapshot; readonly onClose: () => void }) {
  const current = snapshot.document?.product;
  const [error, setError] = useState<string | null>(null);
  const [tubeCode, setTubeCode] = useState(current?.kind === 'tube' ? current.presetCode : '83hn');
  const [materialId, setMaterialId] = useState(current?.kind === 'tube' ? current.materialId : 'photo-paper');
  const [height, setHeight] = useState(current?.kind === 'tube' ? String(current.heightMm / 10).replace('.', ',') : '15');
  const [stickerCode, setStickerCode] = useState(current?.kind === 'sticker' && current.source.kind === 'preset' ? current.source.presetCode : '83hn');
  const [custom, setCustom] = useState(current?.kind === 'sticker' && current.source.kind === 'custom');
  const [shape, setShape] = useState<'circle' | 'square' | 'rect'>(current?.kind === 'sticker' ? current.shape : 'circle');
  const [width, setWidth] = useState(current?.kind === 'sticker' ? String(current.widthMm / 10).replace('.', ',') : '5,8');
  const [customHeight, setCustomHeight] = useState(current?.kind === 'sticker' ? String(current.heightMm / 10).replace('.', ',') : '5,8');

  if (!current) return null;
  const save = async () => {
    const hasPositionedContent = Boolean(snapshot.document
      && (snapshot.document.nodes.length > 0 || snapshot.document.background.kind === 'custom'));
    if (hasPositionedContent && !window.confirm('Cambiar la medida puede modificar el encuadre. Los objetos y el fondo propio conservarán su relación con el centro. ¿Continuar?')) return;
    if (current.kind === 'tube') {
      const result = resolveTubeProduct({ presetCode: tubeCode, materialId, heightInput: height });
      if (!result.ok) {
        setError(result.error.message);
        return;
      }
      await editor.replaceProduct(result.value);
    } else if (!custom) {
      await editor.replaceProduct(createPresetStickerProductV1(getStickerPreset(stickerCode)));
    } else {
      const result = createCustomStickerProductV1({ shape, widthInput: width, heightInput: customHeight });
      if (!result.ok) {
        setError(result.error.message);
        return;
      }
      await editor.replaceProduct(result.value);
    }
    onClose();
  };

  return (
    <Modal title="Cambiar medida" onClose={onClose} footer={<><button type="button" onClick={onClose}>Cancelar</button><button className="primary-action" type="button" onClick={() => void save()}>Aplicar medida</button></>}>
      {current.kind === 'tube' ? (
        <div className="form-stack">
          <label>Diámetro
            <select value={tubeCode} onChange={(event) => setTubeCode(event.target.value)}>
              {TUBE_PRESETS.map((preset) => <option key={preset.code} value={preset.code}>{preset.diameterMm} mm · {preset.code} · desarrollo {preset.developmentBaseMm} mm</option>)}
            </select>
          </label>
          <label>Material
            <select value={materialId} onChange={(event) => setMaterialId(event.target.value as typeof materialId)}>
              {PLOT_MATERIALS.map((material) => <option key={material.id} value={material.id}>{material.label}</option>)}
            </select>
          </label>
          <label>Alto del tubo<div className="unit-input"><input inputMode="decimal" value={height} onChange={(event) => setHeight(event.target.value)} /><span>cm</span></div></label>
          <div className="measure-preview">
            <span>Archivo final</span>
            <strong>{calculateEffectiveDevelopmentMm(getTubePreset(tubeCode), getPlotMaterial(materialId))} × {Math.round(Number(height.replace(',', '.')) * 10) || '—'} mm</strong>
          </div>
        </div>
      ) : (
        <div className="form-stack">
          <SegmentedControl label="Tipo de medida" value={custom ? 'custom' : 'preset'} options={[{ value: 'preset', label: 'Preset' }, { value: 'custom', label: 'Personalizado' }]} onChange={(value) => setCustom(value === 'custom')} />
          {!custom ? (
            <label>Preset
              <select value={stickerCode} onChange={(event) => setStickerCode(event.target.value)}>
                {STICKER_PRESETS.map((preset) => <option key={preset.code} value={preset.code}>{preset.code} · {preset.group} · {preset.widthMm} × {preset.heightMm} mm</option>)}
              </select>
            </label>
          ) : (
            <>
              <SegmentedControl label="Forma" value={shape} options={[{ value: 'circle', label: 'Círculo' }, { value: 'square', label: 'Cuadrado' }, { value: 'rect', label: 'Rectángulo' }]} onChange={setShape} />
              <label>Ancho / diámetro<div className="unit-input"><input inputMode="decimal" value={width} onChange={(event) => setWidth(event.target.value)} /><span>cm</span></div></label>
              {shape === 'rect' ? <label>Alto<div className="unit-input"><input inputMode="decimal" value={customHeight} onChange={(event) => setCustomHeight(event.target.value)} /><span>cm</span></div></label> : null}
            </>
          )}
        </div>
      )}
      {error ? <p className="inline-error" role="alert">{error}</p> : null}
    </Modal>
  );
}
